import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Button,
  ButtonIcon,
  ButtonSelect,
  type FloatingPlacements,
  FooterActionBar,
  Icon,
  Illustration,
  InlineMessage,
  InputField,
  Menu,
  type MenuOption,
  ModalIsland,
  ModalPageHeader,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { ListSkeleton } from '../../../../shared/components/ListViewer';
import {
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_MED,
  CHAR_LIMIT_FOR_SEARCH,
  l,
  type MessageState,
} from '../../../../shared/constants';
import {
  useDebounce,
  useEffectAfterMount,
  useInViewObserver,
} from '../../../../shared/hooks';
import { type ServiceAccountOwners } from '../../../../shared/types';
import { useGetOwnersListMutation } from '../../data-layer/serviceIdentityApi';
import { type OwnerApiItem, type OwnerApiResponse } from '../../hooks';
import { type OwnerType } from '../../hooks/use-loadfilteroptions';

const MAX_OWNER_ASSIGNMENT = 10;
const OWNERS_TYPE = {
  USER: 'user',
  USERGROUP: 'usergroup',
} as const;
const OWNERS_RANK_OPTIONS = [
  {
    label: 'rank.1',
    value: '1',
    isChecked: false,
  },
  {
    label: 'rank.2',
    value: '2',
    isChecked: false,
  },
  {
    label: 'rank.3',
    value: '3',
    isChecked: false,
  },
  {
    label: 'rank.4',
    value: '4',
    isChecked: false,
  },
  {
    label: 'rank.5',
    value: '5',
    isChecked: false,
  },
  {
    label: 'rank.primary.certifier',
    value: '26',
    isChecked: false,
  },
  {
    label: 'rank.secondary.certifier',
    value: '27',
    isChecked: false,
  },
] as const;

interface TrackedListItem extends OwnerApiItem {
  tracker: {
    id: number;
    isChecked: boolean;
    type: string;
    rank: string;
  };
}

interface ManageOwnerListItemProps {
  intl: IntlShape;
  isLoading: boolean;
  listItem: TrackedListItem;
  handleClick: (id: number, t: string) => void;
  handleRankChange: (id: number, type: string, option: string) => void;
  assignedCount: number;
  isAssignedList: boolean;
}

/**
 * Renders a single owner row with icon, name details, rank selector, and add/remove toggle.
 *
 * @param props - {@link ManageOwnerListItemProps}
 * @param props.isLoading - Disables interactions when true.
 * @param props.listItem - The tracked owner data to display.
 * @param props.handleClick - Callback invoked with the tracker id on add/remove toggle.
 * @param props.handleRankChange - Callback invoked with the tracker id and new rank value.
 * @param props.assignedCount - Current number of assigned owners (used for max-limit checks).
 * @param props.isAssignedList - Whether this item is rendered in the assigned list view.
 * @returns A button element representing the owner row.
 */
const ManageOwnerListItem = React.memo(function ManageOwnerListItem({
  intl,
  isLoading,
  listItem,
  handleClick,
  handleRankChange,
  assignedCount,
  isAssignedList,
}: ManageOwnerListItemProps) {
  const {
    firstname,
    lastname,
    displayname,
    username,
    tracker: { id, isChecked, type, rank },
  } = listItem;

  const isLimit = Boolean(assignedCount >= MAX_OWNER_ASSIGNMENT);
  const isDisabled = useMemo(() => {
    if ((isLimit && !isChecked) || isLoading) {
      return true;
    }

    return false;
  }, [isChecked, isLimit, isLoading]);

  // Utils
  const getToolTipText = () => {
    if (isDisabled) {
      return 'state.max.owners.reached';
    }

    if (isAssignedList || isChecked) {
      return 'state.remove';
    }

    return 'state.add';
  };

  const handleItemClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    handleClick(id, type);
  }, [handleClick, id, isDisabled, type]);

  const tooltipConfigs = useMemo(() => {
    const config = { text: '', placement: 'bottom' as FloatingPlacements };

    if (type === OWNERS_TYPE.USER) {
      config.text = `${firstname ?? ''} ${lastname ?? ''} (${username})`;
    }

    return config;
  }, [firstname, lastname, type, username]);

  const activeFilterLabel = OWNERS_RANK_OPTIONS.find((r) => r.value === rank)
    ?.label;

  return (
    <button
      type='button'
      aria-disabled='false'
      tabIndex={isDisabled ? -1 : 0}
      onClick={() => {
        handleItemClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.detail === 1) {
          handleItemClick();
        }
      }}
      className={cx(
        'ManageOwnerListItem',
        'AccountOwnerListItem',
        isChecked && 'AccountOwnerListItem--isChecked',
        isDisabled && 'AccountOwnerListItem--isDisabled',
        isAssignedList && 'AccountOwnerListItem--isAssignedList'
      )}>
      <Box tag='div' className='AccountOwnerListItem-iconWrapper'>
        {type === OWNERS_TYPE.USERGROUP && (
          <ApplicationIcon size='medium' icon='Group' />
        )}
        {type === OWNERS_TYPE.USER && (
          <ApplicationIcon size='medium' icon='User' />
        )}
      </Box>
      <Box tag='div' className='AccountOwnerListItem-contentWrapper'>
        <Typography kind='body3' color={CF_SEC_MED} lineLimit={1}>
          {type === OWNERS_TYPE.USERGROUP && (
            <FormattedMessage
              id='lGcO12'
              defaultMessage='User Group'
              description='User Group'
            />
          )}
          {type === OWNERS_TYPE.USER && (
            <FormattedMessage
              id='ycpzWV'
              defaultMessage='Users'
              description='Heading for the list of users in the Owner list item'
            />
          )}
        </Typography>
        <Typography kind='h4' color={CF_NEU_BLD} lineLimit={1}>
          {type === OWNERS_TYPE.USERGROUP && displayname}
          {type === OWNERS_TYPE.USER && (displayname ?? username)}
        </Typography>
        <Typography
          kind='body2'
          color={CF_NEU_STL}
          lineLimit={1}
          tooltipConfigs={tooltipConfigs}
          showTooltipOnHover>
          {type === OWNERS_TYPE.USER && (
            <FormattedMessage
              id='EH/So1'
              defaultMessage='{firstname} {lastname} ({username})'
              description='first name, last name and username'
              values={{
                firstname: firstname ?? '',
                lastname: lastname ?? '',
                username,
              }}
            />
          )}
        </Typography>
      </Box>
      <Box tag='div' className={cx('AccountOwnerListItem-selectWrapper')}>
        {isAssignedList && (
          <Box tag='div' className='AccountOwnerListItem-rankCell'>
            <Typography kind='body2' color={CF_NEU_BLD}>
              <FormattedMessage
                id='do+b0S'
                defaultMessage='Rank:'
                description='Rank'
              />
            </Typography>
            <Menu
              isPortalEnabled
              options={OWNERS_RANK_OPTIONS.map((r) => ({
                ...r,
                label: intl.formatMessage(l[r.label as MessageState]),
                isChecked: r.value === rank,
              }))}
              minOptionsForSearch={OWNERS_RANK_OPTIONS.length + 1}
              onChange={(selectedOption: MenuOption) => {
                handleRankChange(id, type, selectedOption.value);
              }}
              classNames={{
                dropdownClassName: 'ManageOwnerListItem-rankDropdown',
              }}
              trigger={
                <ButtonSelect
                  kind='rectangle'
                  label={
                    activeFilterLabel
                      ? intl.formatMessage(l[activeFilterLabel as MessageState])
                      : rank
                  }
                  size='small'
                  className='AccountOwnerListItem-rankCellButton'
                />
              }
              size='small'
            />
          </Box>
        )}
        <Tooltip
          trigger={
            <ButtonIcon
              kind='outlinedSecondary'
              isSelected={isChecked && !isAssignedList}
              isDisabled={isDisabled}
              icon={
                <Icon
                  kind={isAssignedList || isChecked ? 'Subtract' : 'Add'}
                  size='xSmall'
                />
              }
              ariaLabel={
                isAssignedList || isChecked
                  ? intl.formatMessage({
                      id: 'sa1shP',
                      defaultMessage: 'Remove',
                      description: 'Remove',
                    })
                  : intl.formatMessage({
                      id: 'F9dR1Q',
                      defaultMessage: 'Add',
                      description: 'Add',
                    })
              }
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();

                handleItemClick();
              }}
              tabIndex={0}
            />
          }
          text={intl.formatMessage(l[getToolTipText()])}
          placement='left'
        />
      </Box>
    </button>
  );
});

interface ManageOwnerActionProps {
  serviceId?: number;
  isModalOpen: boolean;
  handleModalClose: () => void;
  handleSubmit: ({
    ownersList,
  }: {
    ownersList?: {
      name: string;
      id: number;
      rank: number;
      type: string;
      firstname: string | null;
      lastname: string | null;
    }[];
  }) => void;
  isLoading: boolean;
  isDisabled?: boolean;
  currentOwners: ServiceAccountOwners[];
}

const DEFAULT_LIST_STATE = {
  cursor: null as string | null,
  firstCall: true,
  isObserverInView: true,
  isListError: false,
  isListLoading: false,
};

/**
 * Modal for managing owners of a service identity. Provides search and
 * infinite-scroll pagination over available owners with add/remove selection
 * and rank assignment for assigned owners.
 *
 * @param props - {@link ManageOwnerActionProps}
 * @param props.serviceId - The service identity ID to manage owners for.
 * @param props.isModalOpen - Controls modal visibility.
 * @param props.handleModalClose - Callback to close the modal.
 * @param props.handleSubmit - Callback invoked on save with the selected owners list.
 * @param props.isLoading - Shows loading state and disables interactions.
 * @param props.isDisabled - Disables interactive controls when true.
 * @returns A {@link ModalIsland} with owner search, available/assigned tabs, and rank selection.
 */
function ManageOwnerAction({
  serviceId,
  isModalOpen,
  handleModalClose,
  handleSubmit,
  isLoading,
  isDisabled,
  currentOwners,
}: ManageOwnerActionProps) {
  // Hook
  const intl = useIntl();

  // States
  // Controls
  const [assignType, setAssignType] = useState(1);
  const [ownerType, setOwnerType] = useState(1);

  const [searchValue, setSearchValue] = useState('');
  const [searchText, setSearchText] = useState('');

  // List
  const [listState, setListState] = useState(DEFAULT_LIST_STATE);
  const [listRows, setListRows] = useState<TrackedListItem[]>([]);
  const [activeRows, setActiveRows] = useState<TrackedListItem[]>(
    currentOwners.map((o) => ({
      id: o.id,
      type: o.type.toLowerCase() as OwnerType,
      username: o.name,
      firstname: null,
      lastname: null,
      displayname: o.name,
      email: null,
      rank: null,
      isChecked: true,
      tracker: {
        id: o.id,
        isChecked: true,
        type: o.type.toLowerCase() as OwnerType,
        rank: String(o.rank),
      },
    }))
  );

  // Constants
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        !listState.isListLoading &&
          (listState.firstCall || listState.cursor !== null)
      ),
    [listState.isListLoading, listState.firstCall, listState.cursor]
  );
  const isZeroCount = useMemo(
    () =>
      Boolean(listRows.length === 0 && !listState.firstCall && !isValidApiCall),
    [isValidApiCall, listRows.length, listState.firstCall]
  );
  const isFilterApplied = useMemo(
    () => searchText.trim().length > CHAR_LIMIT_FOR_SEARCH,
    [searchText]
  );

  // Hooks
  const [getOwnersList, { isError: isOwnerError }] = useGetOwnersListMutation();

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setListState((prev) => ({ ...prev, isObserverInView: isInView }));
  });

  const debouncedSearchValue = useDebounce<string>(searchValue, 300);

  // Utils
  const handleClick = useCallback(
    (id: number, t: string) => {
      const modifiedList = listRows.map((item) => {
        if (item.tracker.id === id && item.tracker.type === t) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              isChecked: !item.tracker.isChecked,
            },
          };
        }

        return item;
      });

      setListRows(modifiedList);

      const currentItem = modifiedList.find(
        (e) => e.tracker.type === t && e.tracker.id === id
      );
      const targetRow = activeRows.find(
        (e) => e.tracker.type === t && e.tracker.id === id
      );

      if (targetRow && !currentItem?.tracker.isChecked) {
        setActiveRows((prev) =>
          prev.filter((e) => !(e.tracker.type === t && e.tracker.id === id))
        );

        return;
      }

      if (!targetRow && currentItem?.tracker.isChecked) {
        setActiveRows((prev) => [...prev, currentItem]);
      }
    },
    [activeRows, listRows]
  );

  const handleRankChange = useCallback(
    (id: number, t: string, rank: string) => {
      setActiveRows((prev) =>
        prev.map((item) => {
          if (item.tracker.id === id && item.tracker.type === t) {
            return {
              ...item,
              tracker: {
                ...item.tracker,
                rank,
              },
            };
          }

          return item;
        })
      );
    },
    []
  );

  const handleSaveMOList = useCallback(() => {
    handleSubmit({
      ownersList: activeRows.map((o) => ({
        name: o.type === 'user' ? o.username : o.displayname ?? '',
        id: o.id,
        rank: Number(o.tracker.rank),
        type: o.type,
        firstname: o.firstname,
        lastname: o.lastname,
      })),
    });
  }, [activeRows, handleSubmit]);

  // Effects
  useEffect(() => {
    if (
      debouncedSearchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
      debouncedSearchValue.trim().length > 0
    ) {
      return;
    }

    setSearchText(debouncedSearchValue);
  }, [debouncedSearchValue]);

  // List management
  useEffect(() => {
    if (!listState.isObserverInView || !isValidApiCall) {
      return;
    }

    Logger.log('Fetching', listState.cursor);

    setListState((prev) => ({
      ...prev,
      isListLoading: true,
      isListError: false,
    }));

    const payload = {
      serviceId,
      cursor: listState.cursor,
      search: searchText,
      type: ownerType === 1 ? 'user' : 'usergroup',
      max: 20,
    };
    const ownerEncoding = ownerType === 1 ? 'user' : 'usergroup';

    getOwnersList(payload)
      .unwrap()
      .then((response) => {
        const typedResponse = response as OwnerApiResponse;

        if (!typedResponse.success) {
          setListState((prev) => ({
            ...prev,
            cursor: null,
            isListError: true,
          }));

          return;
        }

        const trackedResponse = typedResponse.data.map((item) => ({
          ...item,
          tracker: {
            id: item.id,
            isChecked: activeRows.find(
              (o) =>
                o.tracker.type === ownerEncoding && o.tracker.id === item.id
            )?.tracker.isChecked,
            type: ownerEncoding,
            rank: String(
              item.rank ??
                activeRows.find(
                  (o) =>
                    o.tracker.type === ownerEncoding && o.tracker.id === item.id
                )?.tracker.rank ??
                '1'
            ),
          },
        })) as TrackedListItem[];

        setListRows((prev) => [...prev, ...trackedResponse]);
        setListState((prev) => ({
          ...prev,
          cursor: typedResponse.nextCursor,
          firstCall: false,
        }));

        const checkedResponses = trackedResponse.filter(
          (i) => i.tracker.isChecked
        );

        setActiveRows((prev) => {
          const existingKeys = new Set(
            prev.map((row) => `${row.tracker.id}-${row.tracker.type}`)
          );

          const newRows = checkedResponses.filter((row) => {
            const key = `${row.tracker.id}-${row.tracker.type}`;

            return !existingKeys.has(key);
          });

          return [...prev, ...newRows];
        });
      })
      .catch((error) => {
        Logger.log('Received error while fetching: ', error);

        setListState((prev) => ({
          ...prev,
          cursor: null,
          isListError: true,
        }));
      })
      .finally(() => {
        setListState((prev) => ({
          ...prev,
          isListLoading: false,
          isObserverInView: false,
        }));
      });
  }, [
    activeRows,
    isValidApiCall,
    listState.isObserverInView,
    listState.cursor,
    serviceId,
    searchText,
    ownerType,
    getOwnersList,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffectAfterMount(() => {
    const debounceTimer = setTimeout(() => {
      setListState(DEFAULT_LIST_STATE);
      setListRows([]);
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [searchText, ownerType]);

  useEffect(() => {
    if (activeRows.length === 0 && assignType === 2) {
      setAssignType(1);
    }
  }, [activeRows, assignType]);

  return (
    <ModalIsland
      isOpen={isModalOpen}
      onClose={handleModalClose}
      HeaderComp={
        <ModalPageHeader
          title={intl.formatMessage({
            id: 'ijMkRZ',
            defaultMessage: 'Add Owner(s)',
            description: 'Manage Owners Modal heading',
          })}
          closeHandler={handleModalClose}
          className='ManageOwnerModal-headerWrapper'
        />
      }
      isOutsideClickEnabled={false}
      isCloseButtonVisible={!isLoading}
      isCloseOnEscapeKeyEnabled={!isLoading}
      contentBody={
        <div className='ManageOwnerModal-contentWrapper'>
          <div className='ManageOwnerModal-contentHeadingWrapper'>
            <Typography kind='h2' color={CF_NEU_BLD}>
              <FormattedMessage
                id='3m6QUW'
                defaultMessage='Assign Owners'
                description='Heading for the owner assignment modal where you can assign owners to a service identity'
              />
            </Typography>
            <Typography kind='body2' color={CF_NEU_STL}>
              <FormattedMessage
                id='jW+to6'
                defaultMessage='Limit is {maxCount}, you cannot add more User or User Groups.'
                description='Helper text in the owner assignment modal that indicates the maximum number of owners'
                values={{ maxCount: MAX_OWNER_ASSIGNMENT }}
              />
            </Typography>
          </div>
          <div className='ManageOwnerModal-contentBodyWrapper'>
            <ToggleButtonGroup
              selection={assignType}
              setSelection={setAssignType}
              isDisabled={
                Boolean(isLoading || isDisabled) || listState.isListLoading
              }>
              <ToggleButton
                label={intl.formatMessage({
                  id: 'YXOeL5',
                  defaultMessage: 'Available',
                  description: 'Available',
                })}
                size='medium'
                isDisabled={
                  Boolean(isLoading || isDisabled) || listState.isListLoading
                }
              />
              <ToggleButton
                label={intl.formatMessage(
                  {
                    id: 'tRyPfV',
                    defaultMessage: 'Assigned ({count})',
                    description: 'Assigned',
                  },
                  { count: activeRows.length }
                )}
                size='medium'
                isDisabled={
                  isLoading ||
                  (isDisabled ?? false) ||
                  activeRows.length === 0 ||
                  listState.isListLoading
                }
              />
            </ToggleButtonGroup>
            <div className='ManageOwnerModal-listControlsWrapper'>
              <InputField
                id='link-account-search-input'
                name='search'
                size='medium'
                placeholder={intl.formatMessage({
                  id: 'vQwyG6',
                  defaultMessage: 'Search by name, or description',
                  description: 'Search by name, or description',
                })}
                kind='search'
                value={searchValue}
                setValue={setSearchValue}
                prefixIcon={
                  <Icon kind='Search' size='small' color={CF_NEU_STL} />
                }
                isCritical={
                  searchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
                  searchValue.trim().length > 0
                }
                isDisabled={
                  Boolean(isDisabled ?? isLoading) ||
                  assignType === 2 ||
                  isOwnerError ||
                  listState.isListLoading
                }
                CriticalHelperText={
                  <InlineMessage
                    text={intl.formatMessage({
                      id: 'rMplSY',
                      defaultMessage:
                        'Type more than 2 characters to see results.',
                      description:
                        'Type more than 2 characters to see results.',
                    })}
                    colorTheme='critical'
                    size='small'
                    leftIcon={<Icon kind='AlertCriticalFilled' />}
                  />
                }
              />
              <ToggleButtonGroup
                selection={ownerType}
                setSelection={setOwnerType}
                isDisabled={
                  Boolean(isDisabled ?? isLoading) ||
                  assignType === 2 ||
                  isOwnerError ||
                  listState.isListLoading
                }>
                <ToggleButton
                  size='medium'
                  icon={
                    <Tooltip
                      trigger={<Icon kind='User' />}
                      text={intl.formatMessage({
                        id: 'iWCA9m',
                        defaultMessage: 'User',
                        description: 'User',
                      })}
                      placement='top'
                    />
                  }
                  isDisabled={
                    Boolean(isDisabled ?? isLoading) ||
                    assignType === 2 ||
                    isOwnerError ||
                    listState.isListLoading
                  }
                />
                <ToggleButton
                  size='medium'
                  icon={
                    <Tooltip
                      trigger={<Icon kind='Group' />}
                      text={intl.formatMessage({
                        id: 'lGcO12',
                        defaultMessage: 'User Group',
                        description: 'User Group',
                      })}
                      placement='top'
                    />
                  }
                  isDisabled={
                    Boolean(isDisabled ?? isLoading) ||
                    assignType === 2 ||
                    isOwnerError ||
                    listState.isListLoading
                  }
                />
              </ToggleButtonGroup>
            </div>
            <div className='ManageOwnerModal-listRowWrapper'>
              {assignType === 1 &&
                listRows.map((item) => (
                  <ManageOwnerListItem
                    key={`${item.tracker.id}-${item.tracker.type}`}
                    intl={intl}
                    isLoading={listState.isListLoading}
                    listItem={item}
                    handleClick={handleClick}
                    assignedCount={activeRows.length}
                    isAssignedList={false}
                    handleRankChange={() => {}}
                  />
                ))}
              {assignType === 2 &&
                activeRows.map((item) => (
                  <ManageOwnerListItem
                    key={`${item.tracker.id}-${item.tracker.type}`}
                    intl={intl}
                    isLoading={listState.isListLoading}
                    listItem={item}
                    handleClick={handleClick}
                    handleRankChange={handleRankChange}
                    assignedCount={activeRows.length}
                    isAssignedList
                  />
                ))}
              {listState.isListError && (
                <Box className='ListViewer-errorModal'>
                  <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
                  <Typography kind='body2' color={CF_CRT_MED}>
                    <FormattedMessage
                      id='apz7lc'
                      defaultMessage='System encountered an error. Please contact the Support Administrator'
                      description='System encountered an error. Please contact the Support Administrator'
                    />
                  </Typography>
                </Box>
              )}
              {/* Observer Element */}
              {!listState.isListError &&
              !listState.isListLoading &&
              assignType === 1
                ? listObserverElement
                : null}
              {/* Loading Skeleton */}
              {!listState.isListError && listState.isListLoading && (
                <Box className='ListViewer-loadingModal'>
                  <ListSkeleton count={3} />
                </Box>
              )}
              {!listState.isListError && isZeroCount && (
                <Box tag='div' className='AccessModal-illustrationWrapper'>
                  <Illustration kind='Empty' />
                  <Typography kind='h1'>
                    {isFilterApplied ? (
                      <FormattedMessage
                        id='C0QjhC'
                        defaultMessage='No Results Found!'
                        description='No Results Found!'
                      />
                    ) : (
                      <FormattedMessage
                        id='3uRK0t'
                        defaultMessage='There are no eligible Accounts to select!'
                        description='There are no eligible Accounts to select!'
                      />
                    )}
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      }
      className='ManageOwnerModal'
      classNames={{
        sectionClassName: 'ManageOwnerModal-sectionClass',
        bodyClassName: 'ManageOwnerModal-bodyClass',
      }}
      FooterActionBarComp={
        <FooterActionBar size='large'>
          <div />
          <div className='ManageOwnerModal-footerBtnWrapper'>
            <Button
              kind='ghost'
              isDisabled={Boolean(isLoading || isDisabled)}
              onClick={handleModalClose}>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
            <Button
              kind='filled'
              isDisabled={
                Boolean(isLoading || isDisabled) ||
                isOwnerError ||
                listState.isListLoading
              }
              isLoading={isLoading}
              loaderText={intl.formatMessage({
                id: 'WmDyLh',
                defaultMessage: 'Saving...',
                description: 'Saving',
              })}
              onClick={handleSaveMOList}>
              <FormattedMessage
                id='0CvRZW'
                defaultMessage='Save'
                description='Save'
              />
            </Button>
          </div>
        </FooterActionBar>
      }
    />
  );
}

ManageOwnerAction.defaultProps = {
  serviceId: undefined,
  isDisabled: false,
};

export default ManageOwnerAction;
